if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();

      this.section = this.closest(".js-product");
      this.optionSelectors = this.querySelectorAll(".option-selector");
      this.data = this.getProductData();

      if (!this.section || !this.data || !this.data.product || this.optionSelectors.length === 0) {
        return;
      }

      this.productForm = this.section.querySelector(".js-product-form-main");
      this.variant = this.getSelectedVariant();
      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener("change", this.handleVariantChange.bind(this));
    }

    handleVariantChange(event) {
      this.variant = this.getSelectedVariant();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(event);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(event);

      this.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product,
          },
        })
      );
    }

    updateAddToCartButton() {
      this.productForm = this.section.querySelector(".js-product-form-main");
      if (!this.productForm) return;

      this.addBtn = this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;

      const variantAvailable = this.variant && this.variant.available;
      const unavailableText = this.variant ? theme.strings.noStock : theme.strings.noVariant;
      const label = this.addBtn.querySelector("[data-add-to-cart-label]");
      const price = this.addBtn.querySelector("[data-add-to-cart-price]");
      const priceValue = this.addBtn.querySelector("[data-add-to-cart-price-value]");

      this.addBtn.disabled = !variantAvailable;

      if (!variantAvailable) {
        if (label) {
          label.textContent = unavailableText;
          if (price) price.hidden = true;
        } else {
          this.addBtn.textContent = unavailableText;
        }
        return;
      }

      if (!label) {
        this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        return;
      }

      label.textContent = this.addBtn.dataset.addToCartText;
      if (price) price.hidden = this.addBtn.dataset.showVariantPrice !== "true";

      if (priceValue) {
        const formattedVariant = this.data.formatted[this.variant.id] || {};
        priceValue.innerHTML = formattedVariant.price || "";
      }
    }

    static updateOptionAvailability(option, variantExists, variantAvailable) {
      if (!option) return;

      const unavailableText = variantExists ? theme.strings.noStock : theme.strings.noVariant;
      option.classList.toggle("is-unavailable", !variantAvailable);
      option.classList.toggle("is-nonexistent", !variantExists);

      if (option.classList.contains("custom-select__option")) {
        const message = option.querySelector("em");
        if (message) message.hidden = variantAvailable;
        if (!variantAvailable) {
          if (message) {
            message.textContent = unavailableText;
          } else {
            option.insertAdjacentHTML(
              "beforeend",
              ` <em class="pointer-events-none">${unavailableText}</em>`
            );
          }
        }
        return;
      }

      const label = option.nextElementSibling;
      if (!label) return;

      if (variantAvailable) {
        label.removeAttribute("title");
      } else {
        label.title = unavailableText;
      }
    }

    updateAvailability() {
      if (this.dataset.showAvailability === "false") return;

      const { availabilityMethod } = this.dataset;
      let currentVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();
      if (!currentVariant) currentVariant = { options: selectedOptions };

      if (!currentVariant.options || selectedOptions.length === 0) return;

      if (availabilityMethod === "selection") {
        this.querySelectorAll(".js-option").forEach((option) => {
          VariantPicker.updateOptionAvailability(option, false, false);
        });

        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matches = 0;
            variant.options.forEach((option, optionIndex) => {
              if (option === currentVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matches += 1;
              }
            });

            if (matches === currentVariant.options.length - 1) {
              const options = selector.querySelectorAll(".js-option");
              const matchingOption = Array.from(options).find((option) =>
                selector.dataset.selectorType === "dropdown"
                  ? option.dataset.value === variant.options[selectorIndex]
                  : option.value === variant.options[selectorIndex]
              );
              VariantPicker.updateOptionAvailability(matchingOption, true, variant.available);
            }
          });
        });
        return;
      }

      this.optionSelectors.forEach((selector, selectorIndex) => {
        selector.querySelectorAll('.js-option:not([data-value=""])').forEach((option) => {
          const optionValue =
            selector.dataset.selectorType === "dropdown" ? option.dataset.value : option.value;
          let variantExists = false;
          let variantAvailable = false;

          this.data.product.variants.forEach((variant) => {
            let previousOptionsMatch = 0;
            for (let index = 0; index < selectorIndex; index += 1) {
              if (variant.options[index] === selectedOptions[index] || selectedOptions[index] === null) {
                previousOptionsMatch += 1;
              }
            }

            if (variant.options[selectorIndex] === optionValue && previousOptionsMatch === selectorIndex) {
              variantExists = true;
              if (variant.available) variantAvailable = true;
            }
          });

          VariantPicker.updateOptionAvailability(option, variantExists, variantAvailable);
        });
      });
    }

    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector(".backorder");
      if (!this.backorder) return;

      let hidden = true;
      if (this.variant && this.variant.available) {
        const formattedVariant = this.data.formatted[this.variant.id] || {};
        if (this.variant.inventory_management && formattedVariant.inventory === "none") {
          const productName = this.backorder.querySelector(".backorder__product");
          const title = this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default") ? "" : ` - ${this.variant.title}`;

          if (productName && title) {
            productName.textContent = `${title.textContent}${variantTitle}`;
            hidden = false;
          }
        }
      }

      this.backorder.hidden = hidden;
    }

    static updateLabelText(event) {
      const selector = event.target.closest(".option-selector");
      if (!selector || selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      const value = event.target.nextElementSibling?.querySelector(".js-value");
      if (colorText && value) colorText.textContent = value.textContent;
    }

    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches("quick-add-drawer")) {
        if (typeof this.section.updateMedia === "function") {
          this.section.updateMedia(this.variant.featured_media.id);
        }
        return;
      }

      this.mediaGallery = this.mediaGallery || this.section.querySelector("media-gallery");
      if (!this.mediaGallery || typeof this.mediaGallery.setActiveMedia !== "function") return;

      const media = this.mediaGallery.querySelector(
        `[data-media-id="${this.variant.featured_media.id}"]`
      );
      if (media) this.mediaGallery.setActiveMedia(media, true, true);
    }

    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector("pickup-availability");
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    updatePrice() {
      this.price = this.price || this.section.querySelector(".product-info__price > .price");
      if (!this.price) return;

      if (this.variant) {
        const formattedVariant = this.data.formatted[this.variant.id] || {};
        const currentPrice = this.price.querySelector(".price__current");
        const comparePrice = this.price.querySelector(".price__was");
        const unitPrice = this.price.querySelector(".unit-price");
        const useVipPrice = this.price.hasAttribute("data-vip-discount") && formattedVariant.vipPrice;

        if (currentPrice) currentPrice.innerHTML = useVipPrice ? formattedVariant.vipPrice : formattedVariant.price || "";
        if (comparePrice) {
          comparePrice.hidden = false;
          comparePrice.style.display = "";
          comparePrice.innerHTML = useVipPrice ? formattedVariant.vipCompareAtPrice || "" : formattedVariant.compareAtPrice || "";
        }

        if (this.variant.unit_price_measurement && unitPrice) {
          const unitPriceValue = this.price.querySelector(".unit-price__price");
          const unitPriceUnit = this.price.querySelector(".unit-price__unit");
          const referenceValue = this.variant.unit_price_measurement.reference_value;
          const referenceUnit = this.variant.unit_price_measurement.reference_unit;

          if (unitPriceValue) unitPriceValue.innerHTML = formattedVariant.unitPrice || "";
          if (unitPriceUnit) {
            unitPriceUnit.textContent =
              referenceValue === 1 ? referenceUnit : `${referenceValue} ${referenceUnit}`;
          }
        }

        if (unitPrice) unitPrice.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle("price--on-sale", useVipPrice || this.variant.compare_at_price > this.variant.price);
        this.price.classList.toggle("price--sold-out", !this.variant.available);
      }

      const defaultPrice = this.price.querySelector(".price__default");
      const noVariantPrice = this.price.querySelector(".price__no-variant");
      if (defaultPrice) defaultPrice.hidden = !this.variant;
      if (noVariantPrice) noVariantPrice.hidden = !!this.variant;
    }

    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll(".product-info__weight");
      if (this.weights.length === 0) return;

      const hasWeight = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = hasWeight ? this.data.formatted[this.variant.id]?.weight || "" : "";
        weight.hidden = !hasWeight;
      });
    }

    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll(".product-info__barcode-value");
      if (this.barcodes.length === 0) return;

      const hasBarcode = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = hasBarcode ? this.variant.barcode : "";
        if (barcode.parentNode) barcode.parentNode.hidden = !hasBarcode;
      });
    }

    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");
      if (!this.sku) return;

      const hasSku = this.variant && this.variant.sku;
      this.sku.textContent = hasSku ? this.variant.sku : "";
      if (this.sku.parentNode) this.sku.parentNode.hidden = !hasSku;
    }

    updateUrl(event) {
      if (
        event &&
        event.type === "change" &&
        this.dataset.updateUrl !== "false" &&
        this.variant
      ) {
        window.history.replaceState({}, "", `${this.dataset.url}?variant=${this.variant.id}`);
      }
    }

    updateVariantInput() {
      if (!this.variant) return;

      this.forms =
        this.forms ||
        this.section.querySelectorAll(".js-product-form-main, .js-instalments-form, .js-product-form");

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;

        input.value = this.variant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const selectedOption = selector.querySelector(".custom-select__option[aria-selected='true']");
          const button = selector.querySelector(".custom-select__btn");
          selectedOptions.push(
            selectedOption?.dataset.value || button?.textContent.trim() || null
          );
          return;
        }

        selectedOptions.push(selector.querySelector("input:checked")?.value || null);
      });

      return selectedOptions;
    }

    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      if (!dataEl) return null;

      try {
        return JSON.parse(dataEl.textContent);
      } catch (error) {
        console.warn("Salt Shift: unable to parse product variant data.", error);
        return null;
      }
    }

    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      if (selectedOptions.some((option) => option === null)) return null;

      return this.data.product.variants.find((variant) =>
        variant.options.every((option, index) => option === selectedOptions[index])
      );
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
